import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { RING_GAP_PX, TIP_EDGE_PX, placeTip } from '@/features/assist/spotlight'

/** Widest the tip grows before it wraps, in px (also capped to the viewport). */
const TIP_MAX_WIDTH_PX = 320

function measure(element) {
  const rect = element.getBoundingClientRect()
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height, bottom: rect.bottom }
}

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight }
}

/**
 * Keeps the element's viewport rect (and the viewport size) current through
 * scrolling (any scroll container, hence capture) and resizes, one read per
 * animation frame.
 */
function useElementRect(element) {
  const [state, setState] = useState(() => ({ rect: measure(element), viewport: viewport() }))
  useLayoutEffect(() => {
    let frame = 0
    const update = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => setState({ rect: measure(element), viewport: viewport() }))
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [element])
  return state
}

/**
 * The tip's natural size: measured at `left: 0` with `width: max-content`
 * (capped at `maxWidth`) so the viewport edge can't squeeze it before it is
 * placed, and re-measured whenever the text or the cap changes. `measured`
 * is false until then, while the tip renders hidden at the origin.
 */
function useTipSize(tip, maxWidth) {
  const ref = useRef(null)
  const [state, setState] = useState({ key: null, width: 0, height: 0 })
  const key = `${maxWidth}:${tip}`
  useLayoutEffect(() => {
    if (!ref.current) return
    const { width, height } = ref.current.getBoundingClientRect()
    setState((current) => (current.key === key && current.width === width && current.height === height ? current : { key, width, height }))
  }, [key])
  return [ref, { width: state.width, height: state.height }, state.key === key]
}

/**
 * The ring and its tip. Portalled to <body> and `position: fixed` over the
 * control, so it never shifts the page's layout; `pointer-events-none` so
 * the control underneath still takes the click. Stacked above open menus
 * and popovers (`z-50`) so a ringed menu item isn't hidden; the tip sits
 * below the control, or above it when there is no room, never over it, and
 * is clamped inside the viewport, measured at its natural width first (up
 * to 320 px or the viewport less 16 px). The control's `aria-describedby` points at
 * the tip (`tipId`, set by AssistSpotlight). The pulse is dropped when the
 * viewer prefers reduced motion (checked in JS for the test runner and
 * again with `motion-safe:` for the browser).
 *
 * @param {{element: HTMLElement, tip: string, tipId: string, reducedMotion: boolean}} props
 */
export default function SpotlightRing({ element, tip, tipId, reducedMotion }) {
  const { rect, viewport: view } = useElementRect(element)
  const maxWidth = Math.min(TIP_MAX_WIDTH_PX, view.width - TIP_EDGE_PX * 2)
  const [tipRef, tipSize, measured] = useTipSize(tip, maxWidth)
  const ringStyle = {
    top: rect.top - RING_GAP_PX,
    left: rect.left - RING_GAP_PX,
    width: rect.width + RING_GAP_PX * 2,
    height: rect.height + RING_GAP_PX * 2,
  }
  const { top, left, placement } = placeTip(rect, tipSize, view)
  return createPortal(
    <div data-assist-spotlight="" data-motion={reducedMotion ? 'reduced' : 'full'}>
      <div
        data-testid="assist-spotlight-ring"
        aria-hidden="true"
        style={ringStyle}
        className={`pointer-events-none fixed z-[60] rounded-control ring-2 ring-brand ring-offset-2 ring-offset-surface ${reducedMotion ? '' : 'motion-safe:animate-pulse'}`}
      />
      {tip ? (
        <div
          ref={tipRef}
          id={tipId}
          data-testid="assist-spotlight-tip"
          data-placement={placement}
          style={measured
            ? { top, left, width: 'max-content', maxWidth }
            : { top: 0, left: 0, width: 'max-content', maxWidth, visibility: 'hidden' }}
          className="pointer-events-none fixed z-[60] rounded-control border border-line bg-surface px-3 py-1.5 text-label text-ink shadow-popover"
        >
          {tip}
        </div>
      ) : null}
    </div>,
    document.body
  )
}
